import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Mic, PenLine, BookOpen, Check, ArrowRight, Quote, Shield, ChevronDown, HelpCircle, Feather, Sparkles } from "lucide-react";
import { Wordmark } from "../components/Wordmark";
import { GiftBanner } from "../components/GiftBanner";
import { useCurrency, LOCAL } from "../lib/region";

const steps = [
  { icon: Mic, title: "Tell us a memory", body: "Speak or type – whichever feels easier. We ask warm, interesting questions so you never face a blank page." },
  { icon: PenLine, title: "We help with the writing", body: "Your words become readable memoir prose – always true to what you said, never invented or embellished." },
  { icon: BookOpen, title: "Your book takes shape", body: "Memories gather into chapters, and chapters into a finished memoir you can keep, share or one day print." },
];

const LANDING_FAQ = [
  { q: "How is my finished memoir presented?", a: "As a complete, designed book — a cover (choose a style or create AI artwork), title page, contents, your chapters and your photos placed within them. You can read it in the app and download it as a print-ready 6×9-inch PDF (or a Word document)." },
  { q: "Can I turn it into a real, physical book?", a: "Yes. Your book downloads as a print-ready 6×9-inch PDF — cover, page numbers and photos included — that you can take to any print-on-demand service or local printer for a paperback or hardback. A built-in \u201corder a printed book\u201d option is coming soon." },
  { q: "Do I have to be a good writer?", a: "Not at all. Just speak or type naturally and we shape your words into flowing prose — always yours to edit." },
  { q: "Is there a free trial?", a: "Yes — 7 days free, no card required. Choose a plan afterwards to carry on; everything you've written is saved and waiting." },
  { q: "Can my family help?", a: "Yes. Share a private link and loved ones can add memories about you — no account needed. You review and approve each one before it joins your story." },
  { q: "Who can see my story?", a: "Only you. Your memories are private by default and are never shared publicly." },
];

function LandingFaqRow({ q, a, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-line rounded-2xl bg-white overflow-hidden">
      <button data-testid={testid} onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-4 text-left px-5 py-4 hover:bg-cream/50 transition-colors">
        <span className="font-serif text-lg text-charcoal">{q}</span>
        <ChevronDown size={20} className={`text-sage shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <p className="px-5 pb-5 -mt-1 text-charcoal/85 leading-relaxed">{a}</p>}
    </div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const cur = useCurrency();
  const [showcase, setShowcase] = useState(null);
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    api.get("/showcase").then(({ data }) => setShowcase(data)).catch(() => {});
    api.get("/payments/plans").then(({ data }) => setPlans(data)).catch(() => {});
  }, []);

  const start = () => navigate("/register");

  return (
    <div className="min-h-screen bg-cream grain">
      <GiftBanner />
      {/* Nav */}
      <header className="sticky top-0 z-30 bg-cream/85 backdrop-blur-md border-b border-line">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/logo.png" alt="Write My Story" className="h-10 w-10 object-contain" />
            <Wordmark size="text-lg sm:text-xl" />
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <Link data-testid="landing-nav-faq" to="/faq"
              className="hidden sm:inline text-charcoal/70 hover:text-charcoal font-medium text-sm px-3 py-2 rounded-full transition-colors whitespace-nowrap">FAQ</Link>
            <button data-testid="landing-signin-btn" onClick={() => navigate("/login")}
              className="text-teal font-semibold text-sm px-3 py-2 hover:bg-sagelight rounded-full transition-colors whitespace-nowrap">Sign in</button>
            <button data-testid="landing-cta-nav" onClick={start}
              className="bg-coral hover:bg-coraldark text-white font-semibold text-sm px-4 sm:px-5 py-2.5 rounded-full transition-colors whitespace-nowrap">Start free trial</button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-12 sm:pt-16 pb-10 grid lg:grid-cols-2 gap-10 items-center relative z-10">
        <div className="animate-fade-up">
          <p className="text-xs uppercase tracking-widest text-sage font-semibold">You remember the moments. We'll help you shape the story.</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-charcoal leading-[1.05] mt-4">
            You lived the story. Now tell it.
          </h1>
          <p className="text-lg text-subtle mt-5 leading-relaxed max-w-lg">
            Write My Story gently guides you through your memories, asks the questions that uncover the
            stories behind them, and helps turn them into a memoir you can share with the people you love.
            You don't need to be a writer. You can even just talk.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <button data-testid="landing-hero-cta" onClick={start}
              className="inline-flex items-center justify-center gap-2 bg-coral hover:bg-coraldark text-white px-8 h-14 rounded-full font-semibold text-lg transition-colors">
              Start Writing My Story <ArrowRight size={20} />
            </button>
            <button onClick={() => navigate("/login")}
              className="inline-flex items-center justify-center bg-white border border-line hover:border-sage text-charcoal px-8 h-14 rounded-full font-semibold text-lg transition-colors">
              I already have an account
            </button>
          </div>
          <p className="text-sm text-subtle mt-4">7-day free trial · no commitment · cancel anytime</p>
          <p data-testid="designed-by-writer" className="inline-flex items-center gap-2 text-sm font-semibold text-teal mt-5 bg-sagelight px-4 py-2 rounded-full">
            <Feather size={15} /> Designed by a writer. Powered by AI.
          </p>
        </div>
        <div className="relative animate-fade-up">
          <div className="rounded-3xl overflow-hidden shadow-lg border border-line">
            <img src="/memoirs-on-table.jpg" alt="Three finished memoirs on a table" className="w-full h-72 sm:h-96 object-cover" />
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14 relative z-10">
        <div className="text-center max-w-xl mx-auto">
          <h2 className="font-serif text-3xl sm:text-4xl text-charcoal">How it works</h2>
          <div className="w-12 h-px bg-gold/70 mx-auto mt-4" />
          <p className="text-subtle mt-3">We turn something that feels enormous – "write my life story" – into something easy: answer one interesting question.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6 mt-10">
          {steps.map((s, i) => (
            <div key={s.title} className="bg-white rounded-2xl border border-line p-7 animate-fade-up" style={{ animationDelay: `${i * 60}ms` }}>
              <span className="w-12 h-12 rounded-2xl bg-sagelight text-teal flex items-center justify-center"><s.icon size={24} /></span>
              <h3 className="font-serif text-xl text-charcoal mt-4">{s.title}</h3>
              <p className="text-subtle mt-2 leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Samples */}
      {showcase?.samples?.length > 0 && (
        <section className="bg-cream2 py-14 relative z-10">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="text-center max-w-xl mx-auto">
              <p className="text-xs uppercase tracking-widest text-sage font-semibold">A glimpse of the destination</p>
              <h2 className="font-serif text-3xl sm:text-4xl text-charcoal mt-2">What a finished memoir looks like</h2>
              <div className="w-12 h-px bg-gold/70 mx-auto mt-4" />
            </div>
            <div className="grid md:grid-cols-3 gap-5 mt-10">
              {showcase.samples.map((s) => (
                <article key={s.id} className="bg-white rounded-2xl border border-line p-6 flex flex-col">
                  <span className="inline-flex items-center gap-1.5 text-xs text-sage font-semibold"><BookOpen size={14} /> {s.chapter}</span>
                  <h3 className="font-serif text-xl text-charcoal mt-2">{s.title}</h3>
                  <p className="font-serif text-charcoal/85 leading-relaxed mt-3 flex-1">{s.excerpt}</p>
                  <p className="text-xs text-subtle italic mt-4">{s.byline}</p>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Testimonials */}
      {showcase?.testimonials?.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14 relative z-10">
          <div className="text-center max-w-xl mx-auto">
            <p className="text-xs uppercase tracking-widest text-sage font-semibold">In their words</p>
            <h2 className="font-serif text-3xl sm:text-4xl text-charcoal mt-2">What people are saying</h2>
            <div className="w-12 h-px bg-gold/70 mx-auto mt-4" />
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
            {showcase.testimonials.map((t, i) => (
              <figure key={t.id} className={`rounded-2xl p-6 flex flex-col ${i % 3 === 1 ? "bg-teal text-cream" : "bg-white border border-line"}`}>
                <Quote size={22} className={i % 3 === 1 ? "text-coral" : "text-sage"} />
                <blockquote className={`font-serif text-lg leading-relaxed mt-3 flex-1 ${i % 3 === 1 ? "text-cream" : "text-charcoal"}`}>"{t.quote}"</blockquote>
                <figcaption className={`mt-4 text-sm font-semibold ${i % 3 === 1 ? "text-cream/90" : "text-teal"}`}>
                  {t.name}<span className={`font-normal ${i % 3 === 1 ? "text-cream/60" : "text-subtle"}`}> · {t.context}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>
      )}

      {/* About Juliet */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-14 relative z-10">
        <div className="grid md:grid-cols-5 gap-8 md:gap-10 items-center">
          <div className="md:col-span-2">
            <div className="rounded-3xl overflow-hidden border border-line shadow-sm max-w-xs mx-auto md:mx-0">
              <img src="/juliet.jpg" alt="Juliet Sullivan, founder of Write My Story" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="md:col-span-3">
            <p className="text-xs uppercase tracking-widest text-gold font-semibold">Meet the founder</p>
            <h2 className="font-serif text-3xl sm:text-4xl text-charcoal mt-2">About Juliet</h2>
            <div className="w-12 h-px bg-gold/70 mt-4" />
            <p className="text-subtle leading-relaxed mt-5">
              Juliet Sullivan is a British-Canadian author, former journalist and memoir writer who
              believes everyone has a story worth preserving. Having written her own books and helped
              others bring their life stories to the page, she created Write My Story to make memoir
              writing simple, personal and accessible – even if you've never considered yourself a writer.
            </p>
            <p className="font-serif text-xl text-charcoal mt-5 leading-snug">
              Her philosophy is simple: <span className="text-teal">you bring the memories; Write My Story helps you find the words.</span>
            </p>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="bg-cream2 py-14 relative z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-xl mx-auto">
            <span data-testid="limited-offer-badge" className="inline-flex items-center gap-2 bg-coral/10 text-coral font-semibold text-sm px-4 py-1.5 rounded-full">
              <Sparkles size={15} /> Limited-time launch offer
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl text-charcoal mt-4">Simple, gentle pricing</h2>
            <div className="w-12 h-px bg-gold/70 mx-auto mt-4" />
            <p className="text-subtle mt-3">Lock in our special launch prices while they last. Start with a 7-day free trial. Payments are collected by Stripe, which automatically converts currencies depending on your location.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-5 mt-10 max-w-2xl mx-auto">
            {plans.map((p) => (
              <div key={p.lookup_key} data-testid={`landing-plan-${p.lookup_key}`}
                className={`rounded-2xl p-7 flex flex-col ${p.lookup_key === "wms_yearly" ? "bg-teal text-cream shadow-lg" : "bg-white border border-line"}`}>
                {p.lookup_key === "wms_yearly" && <span className="text-xs font-semibold bg-coral text-white px-2.5 py-1 rounded-full w-fit mb-3">Best value</span>}
                <p className={`font-serif text-xl ${p.lookup_key === "wms_yearly" ? "text-cream" : "text-charcoal"}`}>{p.name}</p>
                <p className="mt-2"><span className="font-serif text-3xl text-coral">{p.price}</span> <span className={p.lookup_key === "wms_yearly" ? "text-cream/70" : "text-subtle"}>{p.cadence}</span></p>
                {cur !== "GBP" && <p className={`text-xs mt-1 ${p.lookup_key === "wms_yearly" ? "text-cream/70" : "text-subtle"}`}>≈ {cur === "USD" ? p.price_usd : p.price_cad} {p.cadence} ({LOCAL[cur].short})</p>}
                <p className={`text-sm mt-3 flex-1 ${p.lookup_key === "wms_yearly" ? "text-cream/80" : "text-subtle"}`}>{p.blurb}</p>
                {p.trial && <p className={`text-xs font-semibold mt-3 ${p.lookup_key === "wms_yearly" ? "text-cream/90" : "text-sage"}`}>Includes 7-day free trial</p>}
                <button data-testid={`landing-plan-cta-${p.lookup_key}`} onClick={start}
                  className={`mt-5 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-semibold text-sm transition-colors ${p.lookup_key === "wms_yearly" ? "bg-coral hover:bg-coraldark text-white" : "bg-teal hover:bg-tealdark text-cream"}`}>
                  {p.recurring ? "Start my free trial" : "Get lifetime access"}
                </button>
                {cur !== "GBP" && (
                  <p data-testid={`landing-cad-reassure-${p.lookup_key}`} className={`text-xs text-center mt-2 ${p.lookup_key === "wms_yearly" ? "text-cream/70" : "text-subtle"}`}>
                    Prices in {cur === "USD" ? "US$" : "CA$"} for {LOCAL[cur].who} customers
                  </p>
                )}
              </div>
            ))}
          </div>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mt-8 text-sm text-subtle">
            <span className="inline-flex items-center gap-1.5"><Check size={15} className="text-teal" /> Unlimited memories & voice</span>
            <span className="inline-flex items-center gap-1.5"><Check size={15} className="text-teal" /> AI interviews & chapters</span>
            <span className="inline-flex items-center gap-1.5"><Shield size={15} className="text-teal" /> Private by default</span>
          </div>
          <div data-testid="landing-gift-cta" className="mt-10 max-w-xl mx-auto rounded-2xl bg-white border border-line p-6 text-center">
            <p className="font-serif text-xl text-charcoal">A gift they'll treasure forever</p>
            <p className="text-subtle mt-1.5 text-sm">The perfect present for Mother's Day, Father's Day, a birthday or Christmas — give a parent or grandparent a full year to tell their story. We'll email them a gift code and simple instructions.</p>
            <a href="/gift" data-testid="landing-gift-link" className="mt-4 inline-flex items-center justify-center gap-2 bg-coral hover:bg-coraldark text-white px-6 py-2.5 rounded-full font-semibold text-sm transition-colors">
              Give as a gift · £69{cur !== "GBP" ? ` (≈ ${LOCAL[cur].gift})` : ""}
            </a>
            <p className="mt-3 text-sm text-subtle">Received a gift? <a href="/redeem" data-testid="landing-redeem-link" className="text-teal font-semibold underline">Redeem your code</a></p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-16 relative z-10" data-testid="landing-faq">
        <div className="text-center">
          <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-sage font-semibold"><HelpCircle size={14} /> Questions</span>
          <h2 className="font-serif text-3xl sm:text-4xl text-charcoal mt-3">Good questions, honest answers</h2>
        </div>
        <div className="mt-8 space-y-3">
          {LANDING_FAQ.map((it, i) => (
            <LandingFaqRow key={it.q} q={it.q} a={it.a} testid={`landing-faq-${i}`} />
          ))}
        </div>
        <div className="text-center mt-6">
          <Link to="/faq" data-testid="landing-faq-seeall" className="inline-flex items-center gap-1.5 text-teal font-semibold hover:underline">See all questions <ArrowRight size={16} /></Link>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-16 text-center relative z-10">
        <h2 className="font-serif text-3xl sm:text-4xl text-charcoal">You've been meaning to write it down. Start today.</h2>
        <p className="text-subtle mt-4 leading-relaxed">
          A memoir app created by author Juliet Sullivan – for anyone who has ever said,
          "I really should write this down one day." One day can be today.
        </p>
        <button data-testid="landing-footer-cta" onClick={start}
          className="mt-8 inline-flex items-center justify-center gap-2 bg-coral hover:bg-coraldark text-white px-8 h-14 rounded-full font-semibold text-lg transition-colors">
          Start my free trial <ArrowRight size={20} />
        </button>
      </section>

      <footer className="border-t border-line py-8 relative z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-subtle">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="Write My Story" className="h-8 w-8 object-contain" />
            <span>Write My Story · a memoir app by Juliet Sullivan</span>
          </div>
          <div className="flex items-center gap-5">
            <Link to="/faq" data-testid="footer-faq-link" className="hover:text-charcoal transition-colors">FAQ</Link>
            <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-charcoal transition-colors">Privacy</Link>
            <Link to="/terms" data-testid="footer-terms-link" className="hover:text-charcoal transition-colors">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
